/**
 * Replace
 *
 * Collects values from elements and replaces {{placeholder}} text throughout the page.
 * Supports text content, href attributes, and automatic year/month injection.
 *
 * Features:
 * - Collects values from any element with data-hs-replace attribute
 * - Replaces {{name}} placeholders in text content and link hrefs
 * - Complete href replacement for links (prevents Webflow "https://" prefix issues)
 * - Supports multiple elements with same name (creates ordered array, joined with ", ")
 * - Conditional hide feature to exclude elements based on class names
 * - Automatic {{year}} and {{month}} replacement
 * - One-time replacement on page load (no ongoing observers)
 *
 * Structure:
 *   <a data-hs-replace="email" href="mailto:info@example.com">info@example.com</a>
 *   <span data-hs-replace="phone">(555) 123-4567</span>
 *   <div data-hs-replace="service">Service 1</div>
 *   <div data-hs-replace="service">Service 2</div>
 *
 * Usage:
 *   Text: "Contact us at {{email}}" → "Contact us at info@example.com"
 *   Link: <a href="{{email}}">Email</a> → <a href="mailto:info@example.com">Email</a>
 *   Array: "Services: {{service}}" → "Services: Service 1, Service 2"
 *   Dynamic: "Copyright {{year}}" → "Copyright 2024"
 */

import { getSelector } from '@utils';

export function init(config: any) {
  function setupReplacements() {
    // Map to store replacements (supports arrays for duplicate names)
    const replacements = new Map<
      string,
      | { text: string | null; href: string | null }
      | Array<{ text: string | null; href: string | null }>
    >();

    // 1. Collect from data-hs-replace elements
    const elementSelector = getSelector(config, 'element');
    const elements = document.querySelectorAll(elementSelector);

    elements.forEach((element) => {
      // Clone the element to avoid modifying the original DOM
      const clonedElement = element.cloneNode(true) as Element;

      // Find and remove elements with data-hs-replace-hide that match their classes
      const hideSelector = getSelector(config, 'hide');
      const hideElements = clonedElement.querySelectorAll(hideSelector);

      hideElements.forEach((hideElement) => {
        const hideAttr = config.attributes.elements.hide.primary.match(/data-hs-replace-hide/)[0];
        const hideValue = hideElement.getAttribute(hideAttr);

        if (hideValue) {
          // Split by spaces to get all class names to check
          const classesToHide = hideValue.split(' ').filter((c: string) => c.trim());

          // Check if element has any of the classes
          const hasMatchingClass = classesToHide.some((className: string) =>
            hideElement.classList.contains(className)
          );

          // Remove element if it has a matching class
          if (hasMatchingClass) {
            hideElement.remove();
          }
        }
      });

      const elementAttr = config.attributes.elements.element.primary.match(/data-hs-replace/)[0];
      const name = element.getAttribute(elementAttr);
      const text = clonedElement.textContent?.trim() || null;
      const href = element.getAttribute('href');

      // Store if name exists and either text or href exists
      if (name && (text || href)) {
        const value = { text, href };

        // If name already exists, convert to array or append to array
        if (replacements.has(name)) {
          const existing = replacements.get(name)!;
          if (Array.isArray(existing)) {
            existing.push(value);
          } else {
            replacements.set(name, [existing, value]);
          }
        } else {
          replacements.set(name, value);
        }
      }
    });

    // 2. Add dynamic year/month replacements
    const currentYear = new Date().getFullYear().toString();
    const monthNames = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    const currentMonth = monthNames[new Date().getMonth()];

    replacements.set('year', { text: currentYear, href: null });
    replacements.set('month', { text: currentMonth, href: null });

    // If no replacements found, exit early
    if (replacements.size === 0) {
      return;
    }

    // 3. Replace text content efficiently using TreeWalker
    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        // Check if any replacement names exist in the text content
        const text = node.textContent || '';
        for (const name of replacements.keys()) {
          if (text.includes(`{{${name}}}`)) {
            return NodeFilter.FILTER_ACCEPT;
          }
        }
        return NodeFilter.FILTER_SKIP;
      },
    });

    const textNodes: Node[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) {
      textNodes.push(node);
    }

    // Replace text in collected nodes
    textNodes.forEach((textNode) => {
      let newText = textNode.textContent || '';
      let hasChanges = false;

      for (const [name, value] of replacements.entries()) {
        const placeholder = `{{${name}}}`;
        if (newText.includes(placeholder)) {
          let replacementValue: string | null = null;

          // Handle array values (join with ", ")
          if (Array.isArray(value)) {
            replacementValue = value
              .map((v) => v.text || v.href)
              .filter(Boolean)
              .join(', ');
          } else {
            // Use text value if available, otherwise use href value
            replacementValue = value.text || value.href;
          }

          if (replacementValue) {
            newText = newText.replace(new RegExp(placeholder, 'g'), replacementValue);
            hasChanges = true;
          }
        }
      }

      if (hasChanges) {
        textNode.textContent = newText;
      }
    });

    // 4. Replace link hrefs
    const links = document.querySelectorAll('a[href]');
    links.forEach((link) => {
      let href = link.getAttribute('href') || '';
      let hasChanges = false;

      for (const [name, value] of replacements.entries()) {
        const placeholder = `{{${name}}}`;
        if (href.includes(placeholder)) {
          let replacementValue: string | null = null;

          // Handle array values - use first item's href or text
          if (Array.isArray(value)) {
            const firstValue = value[0];
            replacementValue = firstValue.href || firstValue.text;
          } else {
            // If value has a dedicated href, completely replace the entire href
            // (prevents Webflow's default "https://" from breaking tel:, mailto:, etc.)
            if (value.href) {
              replacementValue = value.href;
              href = value.href; // Complete replacement, not substitution
              hasChanges = true;
              break; // Only one replacement per link
            }
            // Otherwise, do normal placeholder replacement with text value
            else if (value.text) {
              replacementValue = value.text;
            }
          }

          if (replacementValue && !hasChanges) {
            href = href.replace(new RegExp(placeholder, 'g'), replacementValue);
            hasChanges = true;
          }
        }
      }

      if (hasChanges) {
        link.setAttribute('href', href);
      }
    });

    // 5. Optional debug output (if data-hs-replace-test element exists)
    const testElement = document.querySelector('[data-hs-replace-test]');
    if (testElement) {
      const debugData: Record<
        string,
        {
          placeholder: string;
          value: string;
          text?: string | null;
          href?: string | null;
          type?: string;
          items?: string[];
        }
      > = {};

      for (const [name, value] of replacements.entries()) {
        const placeholder = `{{${name}}}`;

        if (Array.isArray(value)) {
          const displayValue = value
            .map((v) => v.text || v.href)
            .filter(Boolean)
            .join(', ');

          debugData[name] = {
            placeholder,
            value: displayValue,
            type: `Array (${value.length} items)`,
            items: value.map((v) => v.text || v.href || '(empty)'),
          };
        } else {
          const displayValue = value.text || value.href || '(empty)';
          debugData[name] = {
            placeholder,
            value: displayValue,
            ...(value.text && { text: value.text }),
            ...(value.href && { href: value.href }),
          };
        }
      }

      const pre = document.createElement('pre');
      pre.textContent = JSON.stringify(debugData, null, 2);

      testElement.innerHTML = '';
      testElement.appendChild(
        document.createTextNode(`Replace Module Debug (${replacements.size} replacements):`)
      );
      testElement.appendChild(pre);
    }
  }

  setupReplacements();

  return {
    result: 'replace initialized',
    destroy: () => {},
  };
}
